import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import type { AdminUserRow } from '@repo/shared-types';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/src/components/ui/tooltip';
import { useSendResetLink, useVerifyUserEmail } from '../../hooks/useAdminUsers';
import { ImpersonateMenuItem } from './ImpersonateMenuItem';

interface UserRowActionsProps {
  user: AdminUserRow;
  onChangeEmail: (user: AdminUserRow) => void;
}

type ConfirmKind = 'reset' | 'verify' | null;

export function UserRowActions({ user, onChangeEmail }: UserRowActionsProps) {
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const sendReset = useSendResetLink();
  const verifyEmail = useVerifyUserEmail();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="icon" aria-label="Thao tác">
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <ImpersonateMenuItem user={user} />

          {user.isPlaceholder ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Wrapping span: a disabled DropdownMenuItem sets pointer-events:none
                      on itself, so the hover that drives the tooltip has to land on this
                      ancestor instead. */}
                  <span>
                    <DropdownMenuItem disabled>Gửi link đặt lại mật khẩu</DropdownMenuItem>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Tài khoản chưa có email thật, dùng chức năng chuyển thành tài khoản trong phần
                  Thành viên của app.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setConfirm('reset');
              }}
            >
              Gửi link đặt lại mật khẩu
            </DropdownMenuItem>
          )}

          {!user.emailVerified && (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setConfirm('verify');
              }}
            >
              Đánh dấu đã xác thực
            </DropdownMenuItem>
          )}

          <DropdownMenuItem onSelect={() => onChangeEmail(user)}>Đổi email</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirm === 'reset'} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gửi link đặt lại mật khẩu</AlertDialogTitle>
            <AlertDialogDescription>
              Một email đặt lại mật khẩu sẽ được gửi tới <strong>{user.email}</strong>. Không thể
              thu hồi sau khi đã gửi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendReset.isPending}>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              disabled={sendReset.isPending}
              onClick={() => sendReset.mutate(user.id, { onSuccess: () => setConfirm(null) })}
            >
              Gửi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirm === 'verify'} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Đánh dấu đã xác thực</AlertDialogTitle>
            <AlertDialogDescription>
              Email <strong>{user.email}</strong> sẽ được đánh dấu là đã xác thực. Hành động này
              không thể hoàn tác trên giao diện.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={verifyEmail.isPending}>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              disabled={verifyEmail.isPending}
              onClick={() => verifyEmail.mutate(user.id, { onSuccess: () => setConfirm(null) })}
            >
              Xác nhận
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
